// src/components/plataforma/permissoes/PermissoesPage.js
import React, { Fragment, useState, useCallback, useMemo } from 'react';
import {
    ShieldCheck, PlusCircle, Pencil, Trash2,
    Loader2, XCircle, Save, ChevronDown, ChevronRight, MoreVertical
} from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import Pagination from '../../common/Pagination';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoles, useMutateRoles, usePermissoesDisponiveis } from '../../../hooks/useRoles';
import apiClient from '../../../services/api';
import useAlert from '../../../hooks/useAlert';

const PAGE_SIZE = 15;

const FORM_VAZIO = { name: '', description: '', permissions: [] };

const SCOPE_BADGE = {
    matriz: 'badge-danger',
    franquia: 'badge-warning',
    conta: 'badge-info',
    sistema: 'badge-success',
};

const PermissoesPage = () => {
    const { setAlert } = useAlert();
    const queryClient = useQueryClient();

    const [showForm, setShowForm] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [form, setForm] = useState(FORM_VAZIO);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRow, setExpandedRow] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);

    const { data: roles = [], isLoading } = useRoles();
    const { createRole, updateRole } = useMutateRoles();

    // ✅ ACHADO GRAVE corrigido: antes derivava o catálogo só das permissões
    // já atribuídas a algum Role existente — uma permissão nova (ex.:
    // nota_fiscal:*) nunca aparecia aqui até algum Role já tê-la, o que só
    // acontecia através desta própria tela. Catálogo agora vem do backend
    // (config/permissions.js), a lista real e completa.
    const { data: permissoesSistema = [] } = usePermissoesDisponiveis();

    const deletarRole = useMutation({
        mutationFn: (id) => apiClient.delete(`/roles/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            setAlert('Perfil removido com sucesso!', 'success');
        },
        onError: (e) => setAlert(e.response?.data?.error || 'Erro ao remover perfil.', 'danger'),
    });

    const isPending = createRole.isPending || updateRole.isPending;

    const totalRoles = roles.length;
    const paginatedRoles = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return roles.slice(start, start + PAGE_SIZE);
    }, [roles, currentPage]);

    const permGrupos = useMemo(() => permissoesSistema.reduce((acc, perm) => {
        const parts = String(perm).split(':');
        const modulo = parts[0] || 'geral';
        if (!acc[modulo]) acc[modulo] = [];
        acc[modulo].push(perm);
        return acc;
    }, {}), [permissoesSistema]);

    const handleNovoClick = useCallback(() => {
        setEditingRole(null);
        setForm(FORM_VAZIO);
        setShowForm(true);
        setExpandedRow(null);
    }, []);

    const handleEditClick = useCallback((role) => {
        setEditingRole(role);
        setForm({
            name: role.name || '',
            description: role.description || '',
            permissions: role.permissions || [],
        });
        setShowForm(true);
        setExpandedRow(null);
        setSelectedRole(null);
    }, []);

    const handleFechar = useCallback(() => {
        setShowForm(false);
        setEditingRole(null);
        setForm(FORM_VAZIO);
    }, []);

    const handleFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    }, []);

    const handlePermissaoToggle = useCallback((perm) => {
        setForm(prev => {
            const perms = prev.permissions.includes(perm)
                ? prev.permissions.filter(p => p !== perm)
                : [...prev.permissions, perm];
            return { ...prev, permissions: perms };
        });
    }, []);

    const handleSalvar = useCallback((e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            setAlert('O nome do perfil é obrigatório.', 'warning');
            return;
        }
        if (editingRole) {
            updateRole.mutate(
                { id: editingRole._id, roleData: form },
                { onSuccess: handleFechar }
            );
        } else {
            createRole.mutate(form, { onSuccess: handleFechar });
        }
    }, [form, editingRole, createRole, updateRole, handleFechar, setAlert]);

    const handleDeletar = useCallback((role) => {
        if (window.confirm(`Tem certeza que deseja excluir o perfil "${role.name}"? Usuários com este perfil poderão perder acesso.`)) {
            deletarRole.mutate(role._id);
        }
        setSelectedRole(null);
    }, [deletarRole]);

    const drawerActions = selectedRole ? [
        {
            key: 'editar',
            label: 'Editar perfil',
            icon: <Pencil size={20} className="icon" />,
            onClick: () => handleEditClick(selectedRole),
        },
        {
            key: 'excluir',
            label: 'Excluir perfil',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => handleDeletar(selectedRole),
            destructive: true,
            loading: deletarRole.isPending,
        },
    ] : [];

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const toggleRow = useCallback((id) => {
        setExpandedRow(prev => prev === id ? null : id);
    }, []);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<ShieldCheck />}
                    title="Perfis e Permissões"
                    subtitle={!isLoading && !showForm && totalRoles > 0 ? `${totalRoles} ${totalRoles !== 1 ? 'perfis' : 'perfil'}` : null}
                    actions={showForm && (
                        <button onClick={handleFechar} className="action-button action-button-secondary">
                            <XCircle size={18} className="icon"/> Fechar
                        </button>
                    )}
                />

                {showForm && (
                    <div className="content-panel">
                        <form onSubmit={handleSalvar}>
                            <div className="form-section">
                                <h4 className="form-section-title">
                                    {editingRole ? `Editar Perfil: ${editingRole.name}` : 'Novo Perfil de Acesso'}
                                </h4>
                            </div>

                            <div className="form-grid-container-cols-2">
                                <div className="form-group">
                                    <label className="form-label required">Nome do Perfil</label>
                                    <input
                                        name="name"
                                        value={form.name}
                                        onChange={handleFormChange}
                                        className="form-input"
                                        required
                                        placeholder="Ex: Gerente, Atendente..."
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Descrição</label>
                                    <input
                                        name="description"
                                        value={form.description}
                                        onChange={handleFormChange}
                                        className="form-input"
                                        placeholder="Descreva as responsabilidades deste perfil"
                                    />
                                </div>
                            </div>

                            {permissoesSistema.length > 0 && (
                                <div className="form-group">
                                    <label className="form-label">
                                        Permissões
                                        <span style={{ fontWeight: 400, marginLeft: '0.5rem', color: 'var(--color-text-secondary)' }}>
                                            ({form.permissions.length} selecionada{form.permissions.length !== 1 ? 's' : ''})
                                        </span>
                                    </label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                                        {Object.entries(permGrupos).map(([modulo, perms]) => (
                                            <div key={modulo} style={{ background: 'var(--color-bg-alt, #f9fafb)', borderRadius: 8, padding: '0.75rem', border: '1px solid var(--color-border)' }}>
                                                <div style={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-secondary)', marginBottom: '0.5rem' }}>
                                                    {modulo}
                                                </div>
                                                {perms.map(perm => (
                                                    <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={form.permissions.includes(perm)}
                                                            onChange={() => handlePermissaoToggle(perm)}
                                                        />
                                                        <span>{String(perm).split(':')[1] || perm}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="button" onClick={handleFechar} className="action-button action-button-secondary">
                                    <XCircle size={18} className="icon"/> Cancelar
                                </button>
                                <button type="submit" className="action-button action-button-primary" disabled={isPending}>
                                    {isPending ? <Loader2 size={18} className="icon animate-spin"/> : <Save size={18} className="icon"/>}
                                    {editingRole ? 'Salvar Alterações' : 'Criar Perfil'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin"/>
                                <span>Carregando perfis...</span>
                            </div>
                        ) : paginatedRoles.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 32 }}></th>
                                        <th style={{ textAlign: 'left' }}>Perfil</th>
                                        <th style={{ textAlign: 'left' }}>Escopo</th>
                                        <th>Permissões</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRoles.map(role => {
                                        const permsCount = (role.permissions || []).length;
                                        const isExpanded = expandedRow === role._id;
                                        return (
                                            <Fragment key={role._id}>
                                                <tr>
                                                    <td style={{ padding: '0 0 0 0.75rem' }}>
                                                        {permsCount > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleRow(role._id)}
                                                                className="action-button-icon text-gray-500 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title={isExpanded ? 'Recolher permissões' : 'Ver permissões'}
                                                            >
                                                                {isExpanded
                                                                    ? <ChevronDown size={14}/>
                                                                    : <ChevronRight size={14}/>
                                                                }
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{role.name}</td>
                                                    <td style={{ textAlign: 'left' }}>
                                                        {role.scope
                                                            ? <span className={`badge ${SCOPE_BADGE[role.scope] || 'badge-info'}`}>{role.scope}</span>
                                                            : <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                                                        }
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <span className={`badge ${permsCount > 0 ? 'badge-info' : 'badge-warning'}`}>
                                                            {permsCount} {permsCount !== 1 ? 'permissões' : 'permissão'}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <button
                                                            onClick={() => setSelectedRole(role)}
                                                            title="Ações"
                                                            className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <MoreVertical size={18}/>
                                                        </button>
                                                    </td>
                                                </tr>

                                                {isExpanded && permsCount > 0 && (
                                                    <tr>
                                                        <td colSpan={5} style={{ padding: '0 1.25rem 0.75rem 3rem', background: 'var(--color-bg-alt, #f9fafb)' }}>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', paddingTop: '0.5rem' }}>
                                                                {role.permissions.map(perm => (
                                                                    <span key={perm} className="badge badge-info" style={{ fontSize: '0.7rem' }}>
                                                                        {perm}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <ShieldCheck size={48}/>
                                <h3>Nenhum perfil de acesso encontrado</h3>
                                <p>Crie perfis para controlar o acesso dos usuários à plataforma.</p>
                                <button onClick={handleNovoClick} className="action-button action-button-primary" style={{ marginTop: '1rem' }}>
                                    <PlusCircle size={18} className="icon"/> Adicionar Perfil
                                </button>
                            </div>
                        )}
                    </div>

                    <Pagination
                        totalItems={totalRoles}
                        itemsPerPage={PAGE_SIZE}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            <ActionsDrawer
                open={!!selectedRole}
                onClose={() => setSelectedRole(null)}
                title={selectedRole?.name || 'Perfil'}
                subtitle={selectedRole?.scope}
                actions={drawerActions}
            />

            <FabButton
                onClick={handleNovoClick}
                title="Adicionar Perfil"
                visible={!showForm}
                icon={<ShieldCheck size={26} className="icon"/>}
            />
        </Fragment>
    );
};

export default PermissoesPage;
